<template>
  <section class="targets-europe-map ol-card">
    <header class="targets-europe-map__head">
      <div class="targets-europe-map__title-row">
        <h2 class="targets-europe-map__title">
          {{ $t("lang.dashboard.client_space_map_title") }}
        </h2>
        <div class="targets-europe-map__live">
          <span class="targets-europe-map__live-pulse"></span>
          {{ $t("lang.dashboard.client_space_live") }}
        </div>
      </div>
      <!-- Leyenda reservada para iteración futura
      <div class="targets-europe-map__legend">
        <span class="targets-europe-map__legend-item targets-europe-map__legend-item--mail">
          <span class="targets-europe-map__legend-dot"></span>
          {{ $t("lang.dashboard.client_space_legend_email") }}
        </span>
        <span class="targets-europe-map__legend-item targets-europe-map__legend-item--call">
          <span class="targets-europe-map__legend-dot"></span>
          {{ $t("lang.dashboard.client_space_legend_call") }}
        </span>
      </div>
      -->
    </header>

    <div class="targets-europe-map__wrap">
      <div class="targets-europe-map__canvas">
        <div class="targets-europe-map__counters">
          <div
            class="targets-europe-map__counter targets-europe-map__counter--orders"
          >
            <span class="targets-europe-map__cnum">{{ totalTargetsActive }}</span>
            <span class="targets-europe-map__clabel">{{
              $t("lang.dashboard.client_space_active_orders")
            }}</span>
          </div>
          <div class="targets-europe-map__counter">
            <span class="targets-europe-map__cnum">{{ liveEmails }}</span>
            <span class="targets-europe-map__clabel">{{
              $t("lang.dashboard.client_space_emails_today")
            }}</span>
          </div>
          <div class="targets-europe-map__counter">
            <span class="targets-europe-map__cnum">{{ liveCalls }}</span>
            <span class="targets-europe-map__clabel">{{
              $t("lang.dashboard.client_space_calls_today")
            }}</span>
          </div>
        </div>

        <svg
          viewBox="0 0 600 500"
          class="targets-europe-map__svg"
          preserveAspectRatio="xMidYMid meet"
          v-html="europeMapSvg"
        ></svg>

        <transition-group
          name="targets-europe-blip"
          tag="div"
          class="targets-europe-map__blips"
        >
          <span
            v-for="blip in blips"
            :key="blip.id"
            class="targets-europe-map__blip"
            :style="{ left: blip.x + '%', top: blip.y + '%' }"
          >
            <span class="targets-europe-map__blip-core"></span>
            <span class="targets-europe-map__blip-ring"></span>
            <span v-if="blip.label" class="targets-europe-map__blip-tip">{{
              blip.label
            }}</span>
          </span>
        </transition-group>
      </div>
    </div>
  </section>
</template>

<script>
import europeMapSvg from "@/_helpers/europeMapSvg.js";

const MAP_SCALE = 668.2471978818021;
const MAP_TX = 220.28262870161262;
const MAP_TY = 920.007632986365;
const MAP_WIDTH = 600;
const MAP_HEIGHT = 500;
const BLIP_DISPLAY_MS = 3200;
const BLIP_INTERVAL_MS = 1200;
const WORKDAY_START_HOUR = 8;
const WORKDAY_END_HOUR = 18;
const WORKDAY_HOURS = WORKDAY_END_HOUR - WORKDAY_START_HOUR;

/** Linear progress of the workday: 0 before 8h, 1 after 18h. */
function dayProgress() {
  const now = new Date();
  const hours = now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
  if (hours <= WORKDAY_START_HOUR) return 0;
  if (hours >= WORKDAY_END_HOUR) return 1;
  return (hours - WORKDAY_START_HOUR) / WORKDAY_HOURS;
}

function ceilingForTotal(total) {
  const safeTotal = Math.max(0, Number(total) || 0);
  return Math.round(safeTotal * dayProgress());
}

function projectPoint(lon, lat) {
  const l = (lon * Math.PI) / 180;
  const p = (lat * Math.PI) / 180;
  const px = MAP_SCALE * l + MAP_TX;
  const py = MAP_TY - MAP_SCALE * Math.log(Math.tan(Math.PI / 4 + p / 2));
  return {
    x: +((px / MAP_WIDTH) * 100).toFixed(2),
    y: +((py / MAP_HEIGHT) * 100).toFixed(2),
  };
}

function dedupeTargets(targets) {
  const seen = new Set();
  const result = [];

  (targets || []).forEach((target) => {
    const lat = parseFloat(target.latitude);
    const lon = parseFloat(target.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lon)) return;

    const key = lat.toFixed(4) + "_" + lon.toFixed(4);
    if (seen.has(key)) return;

    seen.add(key);
    result.push({
      lat,
      lon,
      label: target.target_name || "",
      target_id: target.target_id,
    });
  });

  return result;
}

export default {
  name: "TargetsEuropeMap",
  props: {
    targetsMap: {
      type: Array,
      default: () => [],
    },
    totalTargetsActive: {
      type: Number,
      default: 0,
    },
    totalEmailsProgrammed: {
      type: Number,
      default: 0,
    },
    callsToday: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      europeMapSvg,
      blips: [],
      blipIndex: 0,
      nextBlipId: 0,
      blipLoopTimer: null,
      blipRemoveTimers: [],
      liveEmails: 0,
      liveCalls: 0,
      emailTickerTimer: null,
      callTickerTimer: null,
    };
  },
  computed: {
    mapPoints() {
      return dedupeTargets(this.targetsMap);
    },
    emailsTotal() {
      return Math.max(0, Number(this.totalEmailsProgrammed) || 0);
    },
    callsTotal() {
      return Math.max(0, Number(this.callsToday) || 0);
    },
  },
  watch: {
    mapPoints: {
      handler() {
        this.restartBlipLoop();
      },
      deep: true,
    },
    emailsTotal() {
      this.reanchorCounters();
    },
    callsTotal() {
      this.reanchorCounters();
    },
  },
  mounted() {
    this.startBlipLoop();
    this.reanchorCounters();
    this.startCounterTickers();
  },
  beforeDestroy() {
    this.stopBlipLoop();
    this.stopCounterTickers();
  },
  methods: {
    reanchorCounters() {
      this.liveEmails = ceilingForTotal(this.emailsTotal);
      this.liveCalls = ceilingForTotal(this.callsTotal);
    },
    startCounterTickers() {
      this.stopCounterTickers();
      this.scheduleEmailTicker();
      this.scheduleCallTicker();
    },
    stopCounterTickers() {
      if (this.emailTickerTimer) {
        clearTimeout(this.emailTickerTimer);
        this.emailTickerTimer = null;
      }
      if (this.callTickerTimer) {
        clearTimeout(this.callTickerTimer);
        this.callTickerTimer = null;
      }
    },
    scheduleEmailTicker() {
      const delay = 400 + Math.random() * 500;
      this.emailTickerTimer = setTimeout(() => {
        const ceiling = ceilingForTotal(this.emailsTotal);
        if (this.liveEmails < ceiling) {
          const inc = 1 + Math.floor(Math.random() * 3);
          this.liveEmails = Math.min(this.liveEmails + inc, ceiling);
        }
        this.scheduleEmailTicker();
      }, delay);
    },
    scheduleCallTicker() {
      const delay = 2500 + Math.random() * 2500;
      this.callTickerTimer = setTimeout(() => {
        const ceiling = ceilingForTotal(this.callsTotal);
        if (this.liveCalls < ceiling) {
          this.liveCalls = Math.min(this.liveCalls + 1, ceiling);
        }
        this.scheduleCallTicker();
      }, delay);
    },
    startBlipLoop() {
      this.stopBlipLoop();
      if (!this.mapPoints.length) return;
      this.showNextBlip();
    },
    restartBlipLoop() {
      this.blipIndex = 0;
      this.blips = [];
      this.startBlipLoop();
    },
    stopBlipLoop() {
      if (this.blipLoopTimer) {
        clearTimeout(this.blipLoopTimer);
        this.blipLoopTimer = null;
      }
      this.blipRemoveTimers.forEach((timer) => clearTimeout(timer));
      this.blipRemoveTimers = [];
    },
    showNextBlip() {
      const points = this.mapPoints;
      if (!points.length) return;

      const point = points[this.blipIndex];
      this.blipIndex = (this.blipIndex + 1) % points.length;

      const position = projectPoint(point.lon, point.lat);
      const id = this.nextBlipId++;
      this.blips.push({
        id,
        x: position.x,
        y: position.y,
        label: point.label,
      });

      const removeTimer = setTimeout(() => {
        this.blips = this.blips.filter((blip) => blip.id !== id);
      }, BLIP_DISPLAY_MS);
      this.blipRemoveTimers.push(removeTimer);

      this.blipLoopTimer = setTimeout(() => {
        this.showNextBlip();
      }, BLIP_INTERVAL_MS);
    },
  },
};
</script>

<style scoped>
.targets-europe-map {
  --ol-radius: 14px;
  --ol-primary: #5054eb;
  --ol-primary-soft: #f0f1fe;
  --ol-primary-dark: #3d42b8;
  --ol-surface: #ffffff;
  --ol-text-2: #5a5a6e;
  --ol-border: #e8e8ee;
  --ol-green-bg: #e1f5ee;
  --ol-green-fg: #0f6e56;
  --ol-blip-color: #5054eb;

  background: var(--ol-surface);
  border: 1px solid var(--ol-border);
  border-radius: var(--ol-radius);
  padding: clamp(18px, 2.4vw, 26px);
  box-shadow: 0 1px 2px rgba(28, 28, 40, 0.04),
    0 12px 30px rgba(28, 28, 40, 0.05);
}

.targets-europe-map__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.targets-europe-map__title-row {
  display: flex;
  align-items: center;
  gap: 11px;
  flex-wrap: wrap;
}

.targets-europe-map__title {
  font-weight: 700;
  font-size: 17px;
  margin: 0;
  letter-spacing: -0.02em;
}

.targets-europe-map__live {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--ol-green-bg);
  color: var(--ol-green-fg);
  padding: 7px 14px;
  border-radius: 100px;
  font-weight: 600;
  font-size: 13px;
}

.targets-europe-map__live-pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ol-green-fg);
  animation: targets-europe-pulse 1.6s infinite;
}

@keyframes targets-europe-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(15, 110, 86, 0.45);
  }

  70% {
    box-shadow: 0 0 0 8px rgba(15, 110, 86, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(15, 110, 86, 0);
  }
}

.targets-europe-map__wrap {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.targets-europe-map__canvas {
  position: relative;
  flex: 1;
  min-width: 280px;
  aspect-ratio: 16 / 10;
  background: radial-gradient(
      circle at 50% 38%,
      var(--ol-primary-soft),
      transparent 70%
    ),
    #fcfcfe;
  border: 1px solid var(--ol-border);
  border-radius: 14px;
  overflow: hidden;
}

.targets-europe-map__svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.targets-europe-map__svg >>> .ol-map__country {
  fill: #ebebfd;
  stroke: #d0d2f5;
  stroke-width: 0.8;
  stroke-linejoin: round;
}

.targets-europe-map__counters {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.targets-europe-map__counter {
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 6px 20px rgba(28, 28, 40, 0.1);
  min-width: 132px;
}

.targets-europe-map__counter--orders {
  border-color: var(--ol-primary);
  background: rgba(80, 84, 235, 0.1);
}

.targets-europe-map__cnum {
  font-weight: 800;
  font-size: 26px;
  color: var(--ol-primary);
  line-height: 1;
  display: block;
  font-variant-numeric: tabular-nums;
  transition: color 0.18s ease;
}

.targets-europe-map__counter--orders .targets-europe-map__cnum {
  color: var(--ol-primary-dark);
}

.targets-europe-map__clabel {
  font-size: 11px;
  color: var(--ol-text-2);
  margin-top: 3px;
  display: block;
}

.targets-europe-map__blips {
  position: absolute;
  inset: 0;
}

.targets-europe-map__blip {
  position: absolute;
  transform: translate(-50%, -50%);
  width: 13px;
  height: 13px;
}

.targets-europe-map__blip-core {
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  z-index: 2;
  background: var(--ol-blip-color);
  box-shadow: 0 0 8px rgba(80, 84, 235, 0.7);
}

.targets-europe-map__blip-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  z-index: 1;
  border: 2px solid rgba(80, 84, 235, 0.5);
  animation: targets-europe-blip-ring 1.8s ease-out infinite;
}

@keyframes targets-europe-blip-ring {
  0% {
    transform: scale(0.6);
    opacity: 1;
  }

  100% {
    transform: scale(3);
    opacity: 0;
  }
}

.targets-europe-map__blip-tip {
  position: absolute;
  bottom: 135%;
  left: 50%;
  transform: translateX(-50%);
  background: #1c1c28;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 7px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}

.targets-europe-map__blip:hover .targets-europe-map__blip-tip {
  opacity: 1;
}

.targets-europe-blip-enter-active {
  transition: all 0.4s cubic-bezier(0.2, 1.4, 0.4, 1);
}

.targets-europe-blip-leave-active {
  transition: all 0.5s ease;
}

.targets-europe-blip-enter,
.targets-europe-blip-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0);
}
</style>
